
import { IError, IResponse, IResponseEvent } from "@core/data-access/model";
import { IPermissionService } from "../page-service";
import { IAnonymousPageObjectTreeService } from "./anonymous-page-object-tree";
import { IAnonymousResponseUser } from "../response-user";
import { envConfig } from "@env/env-config";
import { CatchError } from "@core/error/catch-error";
import { BusinessException } from "@core/error/business-exception";
import { HttpRequestUtil, IRequestConfig } from "@core/utils/http-request-util";
import { IAnonymousRestService } from "@modules/user/rest/anonymous-rest";
import { IMessageDialog } from "@qpUIs/qp-message-dialog/qp-message-dialog.interface";
import { IMessageDialogLevel, IMessageDialogOptions, IUnhandleMessageDialog } from "@qpUIs/qp-message-dialog/qp-message-dialog-options.interface";
import { PromptTypeEnum } from "@modules/base/decorator/error/PromptType.enum";

export class AnonymousPagePermissionService implements IPermissionService {

    public static readonly $inject: Array<string> = ["$objectTree", "anonymousRestService", "$translate", "pageContext", "$injector", "qpMessageDialog", "$q"];
    
    public rootPath: string;

    constructor(
        public $objectTree: IAnonymousPageObjectTreeService,
        public anonymousRestService: IAnonymousRestService,
        public $translate: angular.translate.ITranslateService,
        public pageContext: qpmn.main.IPageContext,
        public $injector: angular.auto.IInjectorService,
        public qpMessageDialog: IMessageDialog,
        public $q: angular.IQService
    ) {

    }
    // 联系我们的邮箱地址
    private supportmail: string = envConfig.supportEmail;
    /**
     * @description 校验用户token有效期(异步处理)
     * @param successFunc 
     */
    public checkAuthorization(successFunc: Function): void {
        let currentToken = this.$objectTree.token?.access_token;
        // 有token且没有过期则直接初始化上下文
        if (currentToken && !this.$objectTree.isExpire()) {
            successFunc();
        } else if (currentToken && this.$objectTree.isExpire()) {
            /********************************************************************************************
             *  为了以后分支处理发生改变时便于逻辑梳理,因此在这里允许代码冗余,按业务分支编写逻辑处理
             ********************************************************************************************/

            // 有token且已过期,重置为匿名用户token
            this.resetAnonymousToken(() => {
                successFunc();
            })
        } else {
            // 无token,获取匿名token，初始化上下文
            this.resetAnonymousToken(() => {
                successFunc();
            })
        }
    }

    /**
     * @description 校验用户token有效期(同步请求)
     * @param successFunc 
     */
    public syncCheckAuthorization(successFunc?: Function): void {
        let currentToken = this.$objectTree.token?.access_token;
        // 有token且没有过期则直接初始化上下文
        if (currentToken && !this.$objectTree.isExpire()) {
            if (successFunc) {
                successFunc();
            }
        } else if (currentToken && this.$objectTree.isExpire()) {
            /********************************************************************************************
             *  为了以后分支处理发生改变时便于逻辑梳理,因此在这里允许代码冗余,按业务分支编写逻辑处理
             ********************************************************************************************/
            // 有token且已过期,重置为匿名用户token            
            this.syncResetAnonymousToken(() => {
                if (successFunc) {
                    successFunc();
                }
            })
        } else {
            // 无token,获取匿名token，初始化上下文
            this.syncResetAnonymousToken(() => {
                if (successFunc) {
                    successFunc();
                }
            })
        }
    }

    /**
     * @description 重置匿名用户Token(使用同步请求)
     * 
     */
    public syncResetAnonymousToken(successCallback) {
        // 获取匿名用户id, 如果发起请求的anonymousUserId为 0 则表示新建匿名用户
        const anonymousUserId: number = this.$objectTree.getAnonymousUserId() || 0;

        const rootPath = envConfig.appPath();
        // 匿名登录请求配置
        const loginCfg: IRequestConfig = {
            method: "get",
            url: `${rootPath}/cgp-rest/anonymousUser/${anonymousUserId}/token`
        }
        const httpUtil = new HttpRequestUtil();
        // 发起同步请求
        httpUtil.request<IResponse<IAnonymousResponseUser>>(loginCfg, (result: IResponse<IAnonymousResponseUser>) => {
            const userInfo = result.data;
            // Token信息存至缓存
            // this.$objectTree.setLogin({
            //     firstName: userInfo.firstName,
            //     userName: userInfo.username,
            //     token: userInfo.access_token,
            //     tokenType: userInfo.token_type,
            //     isAnonymous: true,
            //     anonymousUserId: userInfo.anonymousUserId,
            //     expire: userInfo.expires_in,
            //     partnerType: userInfo.partnerType,
            // })
            this.$objectTree.setAnonymousLogin({
                token: userInfo.access_token,
                tokenType: userInfo.token_type,
                isAnonymous: true,
                anonymousUserId: userInfo.abc,
                expire: userInfo.expires_in,
            })
            if (successCallback) {
                successCallback();
            }
        }, (errorEvent) => {
            // TODO 匿名登录请求失败处理
        });
    }

    private readonly PROMPT_DEFAULT_CONFIG = {
        // 是否反向显示confirm和取消按钮
        reverseButtons: true,
        // 是否显示取消按钮
        showCancelButton: false,
        // 取消按钮的自定义class类
        cancelButtonClass: 'alert-login-cancel',
        // 确认按钮的自定义class类
        confirmButtonClass: 'alert-login-confirm',
        // 为按钮添加默认的swal2样式
        buttonsStyling: true,
        // 是否允许点击对话框外部来关闭对话框
        allowOutsideClick: false,
        // 是否允许按下Esc键来关闭对话框
        allowEscapeKey: false,
        title: "Permission invalid",
        content: "Go to login?",
        cancelButtonText: "CANCEL",
        confirmButtonText: "OK"
    }

    // @CatchError()
    // public catchErrorFail(error: IError) {
    //     if (angular.isUndefined(error.code)) {
    //         throw new BusinessException("A102B002");
    //     }
    //     if (error.message) {
    //         throw new BusinessException(error.code, error.message);
    //     } else {
    //         throw new BusinessException(error.code);
    //     }
    // }

    private goToLogin(isRecordCurrentPageHref: boolean = true) {
        let root: string;
        if (this.rootPath != undefined && this.rootPath != "") {
            root = this.rootPath;
        } else if (this.pageContext != undefined && this.pageContext.rootPath != undefined && this.pageContext.rootPath != "") {
            root = this.pageContext.rootPath
        } else {
            root = envConfig.appPath();
        }

        if (root.endsWith("/")) {
            root = root.slice(0, -1);
        }

        // 是否记录当前页面href
        if (isRecordCurrentPageHref) {
            window.location.href = root + "/partner/login/" + "?" + encodeURIComponent(location.href);
        } else {
            window.location.href = root + "/partner/login/";
        }
    }

    /**
     * @description 请求失败处理
     * 
     */
    public onRequestFail<T extends IError>(e: IResponseEvent<T>) {
        // e类型判断
        if (e != null && typeof e === "object") {
            //没有访问权限
            if (e.status == 401) {
                // 检查当前是否是实名用户
                const isAnonymous = this.$objectTree.isAnonymous();

                if (isAnonymous) {
                    // 匿名用户:直接重新获取匿名用户Token
                    this.resetAnonymousToken();
                } else {
                    let config = angular.copy(this.PROMPT_DEFAULT_CONFIG);
                    config.showCancelButton = true;
                    // 权限不足标题
                    const authTitle = this.$translate.instant("A107B002");
                    // 提示文本
                    const authContent = this.$translate.instant("A107B003");

                    // 实名用户:告知权限失效，并询问是否前往登录(若不前往登录则重新获取匿名Token)
                    window.qpAlertPrompt.question(authTitle, authContent, {
                        fn: () => {
                            this.$objectTree.setAnonymous(true);
                            // 记录当前页面地址并跳转至登录页
                            this.goToLogin();
                        }
                    }, { fn: () => { this.resetAnonymousToken(); } }, config);
                }
            } else if (e.status == 404) {
                const connectionTitle = this.$translate.instant("A102B003");
                const connectionContent = this.$translate.instant("A102B004");
                // 请求404报错处理
                window.qpAlertPrompt.error(connectionTitle, `${connectionContent} ${this.supportmail}`);
            } else if (e.status == 504) {
                const timeoutTitle = this.$translate.instant("A102B005");
                const timeoutContent = this.$translate.instant("A102B006");
                window.qpAlertPrompt.warning(timeoutTitle, timeoutContent);
            } else if (e.status == -1) {
                // 网络断开处理
                const timeoutTitle = this.$translate.instant("NETWORK_UNAVAILABLE_TITLE");
                const timeoutContent = this.$translate.instant("NETWORK_UNAVAILABLE_CONTENT");
                window.qpAlertPrompt.info(timeoutTitle, timeoutContent);
            } else {
                // this.catchErrorFail(e.data.data);
                this.commonErrorHandle(e);
            }
        } else {
            this.commonErrorHandle(e);
        }
    }

    // @CatchError()
    public onRequestFailed<T extends IError>(e: IResponseEvent<T>): angular.IPromise<T> {
        let $translate = this.$translate;
        let $msgDialog = this.qpMessageDialog;
        let defer = this.$q.defer<T>();

        if (e != null && typeof e === "object") {
            //没有访问权限
            if (e.status == 401) {
                // 匿名用户id, 如果发起请求的anonymousUserId为 0 则表示新建匿名用户
                let anonymousUserId: number = this.$objectTree.getAnonymousUserId();

                // 检查当前是否是实名用户
                let isAnonymous = this.$objectTree.isAnonymous();

                // 匿名用户处理
                if (isAnonymous) {
                    this.resetAnonymousToken();
                } else {
                    // 此处builder定制页需要使用
                    // 实名用户处理
                    $msgDialog.info({
                        title: $translate.instant("MISSING_AUTHENTICATION_TITLE"),
                        content: $translate.instant("MISSING_AUTHENTICATION_CONTENT"),
                        buttons: [
                            "ok",
                            {
                                key: "cancel",
                                displayName: "cancel",
                                style: "text"
                            }
                        ]
                    }).then(res => {
                        if (res == "ok") {
                            this.$objectTree.setAnonymous(true);
                            // 记录当前页面地址并跳转至登录页
                            this.goToLogin();
                        } else if (res == "cancel") {
                            this.resetAnonymousToken();
                        }
                    })
                }
            } else if (e.status == 404) {
                // 请求404报错处理
                $msgDialog.info({
                    title: $translate.instant("SYSTEM_ERROR_TITLE"),
                    content: $translate.instant("SYSTEM_ERROR_CONTENT", { supportmail: envConfig.supportEmail })
                })
            } else if (e.status == 504) {
                $msgDialog.info({
                    title: $translate.instant("NETWORK_CONNECTION_TIMEOUT_TITLE"),
                    content: $translate.instant("NETWORK_CONNECTION_TIMEOUT_CONTENT")
                })
            } else if (e.status == 200) {
                // 不同Partner不可访问
                if (e.data?.data?.code == "104000001") {
                    $msgDialog.info({
                        title: $translate.instant("LACKING_PERMISSION_TITLE"),
                        content: $translate.instant("LACKING_PERMISSION_CONTENT"),
                        buttons: [
                            "ok"
                        ]
                    }).then(res => {
                        if (res == "ok") {
                            // 跳转至登录页
                            this.goToLogin(false);
                        }
                    })
                } else {
                    defer.resolve(e.data.data);
                }
            } else if (e.status == -1) {
                $msgDialog.info({
                    title: $translate.instant("NETWORK_UNAVAILABLE_TITLE"),
                    content: $translate.instant("NETWORK_UNAVAILABLE_CONTENT")
                })
            } else {
                this.commonErrorHandle(e);
            }
        } else {
            this.commonErrorHandle(e);
        }

        return defer.promise;
    }

    public getUnhandleErrorMsgConfig(): IUnhandleMessageDialog {
        let $translate = this.$injector.get<angular.translate.ITranslateService>("$translate");

        let title: qpLanguage.keys = "UNEXPECTED_ERROR_TITLE";
        let key: qpLanguage.keys = "UNEXPECTED_ERROR_CONTENT";

        let msgConfigs = {
            title: $translate.instant(title),
            content: $translate.instant(key, { supportmail: this.supportmail }),
        }

        return msgConfigs;
    }

    public openUnhandleErrorMsgDialog(msgLevel: IMessageDialogLevel = "info"): void {
        let $translate = this.$translate;
        let $msgDialog = this.qpMessageDialog;

        let title: qpLanguage.keys = "UNEXPECTED_ERROR_TITLE";
        let key: qpLanguage.keys = "UNEXPECTED_ERROR_CONTENT";

        let msgConfigs = {
            title: $translate.instant(title),
            content: $translate.instant(key, { supportmail: this.supportmail }),
        }

        $msgDialog[msgLevel](msgConfigs);
    }

    /**
     * @description 重置当前匿名
     * 
     * @param successCallback 
     * @param failCallback 
     */
    public resetAnonymousToken(successCallback?, failCallback?) {
        // 获取匿名用户id, 如果发起请求的anonymousUserId为 0 则表示新建匿名用户
        let anonymousUserId: number = this.$objectTree.getAnonymousUserId() || 0;

        this.anonymousRestService.login(anonymousUserId).then((tokenResp: IResponse<IAnonymousResponseUser>) => {
            // Token信息存至缓存
            // this.$objectTree.setLogin({
            //     firstName: tokenResp.data.firstName,
            //     userName: tokenResp.data.username,
            //     token: tokenResp.data.access_token,
            //     tokenType: tokenResp.data.token_type,
            //     isAnonymous: true,
            //     anonymousUserId: tokenResp.data.anonymousUserId,
            //     expire: tokenResp.data.expires_in,
            //     partnerType: tokenResp.data.partnerType,
            // })
            this.$objectTree.setAnonymousLogin({
                token: tokenResp.data.access_token,
                tokenType: tokenResp.data.token_type,
                isAnonymous: true,
                anonymousUserId: tokenResp.data.abc,
                expire: tokenResp.data.expires_in,
            })
            if (successCallback) {
                successCallback();
            }
            // TODO: 是否需要reload
            window.location.reload();
        }, (failResp) => {
            // 登录失败处理
            //TODO userId不存在处理,暂时未知什么原因会导致这个问题发生,在这做兼容处理,userId不存在时,重新执行匿名登录,后续通过Sentry采集异常
            if (failResp.status === 200 && failResp.data && !angular.isUndefined(failResp.data.success) && !failResp.data.success && !angular.isUndefined(failResp.data.data.code) && failResp.data.data.code === 3000001) {

                // 清除缓存中记录的匿名用户id
                this.$objectTree.removeCache("AnonymousUserId");
                // 重新登录
                this.resetAnonymousToken(successCallback, failCallback);
            } else {
                this.onRequestFail(failResp);
            }
        })
    }


    /**
     * @description 
     * 登录提示
     * 由于匿名页面也会存在部分需登录后才能使用的功能,因此在使用前需告知用户当前操作需要登录
     */
    public authPrompt(promptConfig?: qpmn.base.decorator.error.CatchErrorConfig) {
        // 提示文本
        const authTitle = promptConfig && promptConfig.title ? promptConfig.title : this.$translate.instant("A102B007");
        const authContent = this.$translate.instant("A107B005");
        let extendCfg = promptConfig && promptConfig.extendCfg ? promptConfig.extendCfg : this.PROMPT_DEFAULT_CONFIG;
        window.qpAlertPrompt.question(authTitle, authContent, {
            fn: () => {
                // 记录当前页面地址并跳转至登录页
                this.goToLogin();
            }
        }, {}, extendCfg);
    }

    /**
     * @description 通用错误提示
     */
    public commonErrorHandle<T extends IError>(e: IResponseEvent<T>) {
        if (this.$injector) {
            let $translate = this.$translate;
            let $msgDialog = this.qpMessageDialog;

            if ($msgDialog && $translate != null) {
                if (e && e.data && e.data.data) {
                    let content = e.data.data.message;
                    const errorMsg = $translate.instant(e.data.data.code);
                    if (errorMsg && errorMsg != String(e.data.data.code)) {
                        content = errorMsg;
                    }

                    $msgDialog.info({
                        title: $translate.instant("UNEXPECTED_ERROR_TITLE"),
                        content: $translate.instant("UNEXPECTED_ERROR_CONTENT", { supportmail: this.supportmail }),
                        code: e.data.data.code,
                        msg: content
                    })
                } else if (typeof e === "string") {
                    $msgDialog.info({
                        title: $translate.instant("UNEXPECTED_ERROR_TITLE"),
                        content: $translate.instant("UNEXPECTED_ERROR_CONTENT", { supportmail: this.supportmail }),
                        code: e,
                        msg: e
                    })
                } else {
                    $msgDialog.info({
                        title: $translate.instant("UNEXPECTED_ERROR_TITLE"),
                        content: $translate.instant("UNEXPECTED_ERROR_CONTENT", { supportmail: this.supportmail })
                    })
                }
            } else {
                if (e && e.data && e.data.data) {
                    let content = e.data.data.message;
                    const errorMsg = $translate.instant(e.data.data.code);
                    if (errorMsg && errorMsg != String(e.data.data.code)) {
                        content = errorMsg;
                    }

                    window.qpAlertPrompt[PromptTypeEnum.INFO](
                        $translate.instant("UNEXPECTED_ERROR_TITLE"),
                        content || $translate.instant("UNEXPECTED_ERROR_CONTENT", { supportmail: this.supportmail })
                    )
                } else {
                    window.qpAlertPrompt[PromptTypeEnum.INFO](
                        $translate.instant("UNEXPECTED_ERROR_TITLE"),
                        $translate.instant("UNEXPECTED_ERROR_CONTENT", { supportmail: this.supportmail })
                    )
                }
            }
        } else {
            window.qpAlertPrompt[PromptTypeEnum.INFO](
                "Something Went Wrong",
                `We encountered an issue. Please refresh the page and try again. If the problem persists, please contact our support team at ${this.supportmail}`
            )
        }
    }

}